import React from "react";
import MySwitch from "./MySwitch";

interface TableAction<T> {
  label: string;
  onClick: (row: T) => void;
  className?: string;
  customButton?: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  disabledTooltip?: string;
}

interface TableViewProps<T> {
  data: T[];
  columns?: (keyof T)[];
  columnLabels?: Partial<Record<keyof T, string>>;
  className?: string;
  actions?: TableAction<T>[];
  handleToggle?: (row: T, column: keyof T) => void;
  switchColumns?: (keyof T)[];
  columnWidths?: Partial<Record<keyof T, string>>;
  emptyMessage?: string;
  isLoading?: boolean;
  onRowClick?: (row: T) => void;
  sortable?: boolean;
  onSort?: (column: keyof T, direction: 'asc' | 'desc') => void;
}

export function MyTableViewJSON<T extends Record<string, any>>({
  data,
  columns,
  columnLabels,
  className = "",
  actions = [],
  handleToggle,
  switchColumns = [],
  columnWidths = {},
  emptyMessage = "No data available",
  isLoading = false,
  onRowClick,
  sortable = false,
  onSort,
}: TableViewProps<T>) {
  const [sortColumn, setSortColumn] = React.useState<keyof T | null>(null);
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');

  const handleSort = (col: keyof T) => {
    if (!sortable || !onSort) return;
    
    const newDirection = sortColumn === col && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortColumn(col);
    setSortDirection(newDirection);
    onSort(col, newDirection);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-gray-600 dark:text-gray-400">Loading...</span>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 dark:text-gray-400">{emptyMessage}</p>
      </div>
    );
  }

  const tableColumns = columns || (Object.keys(data[0]) as (keyof T)[]);

  return (
    <div className={`overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 ${className}`}>
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 table-auto w-full">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            {actions.length > 0 && (
              <th className="px-2 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300 w-12 sticky left-0 bg-gray-50 dark:bg-gray-800 z-10">
                <span className="sr-only">Actions</span>
                <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </th>
            )}
            {tableColumns.map((col) => (
              <th
                key={String(col)}
                onClick={() => handleSort(col)}
                className={`
                  px-3 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300 
                  whitespace-nowrap ${columnWidths[col] ?? ""}
                  ${sortable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors' : ''}
                  select-none
                `}
              >
                <div className="flex items-center gap-1">
                  {columnLabels?.[col] ??
                    String(col)
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                  {sortable && sortColumn === col && (
                    <svg 
                      className={`w-4 h-4 transition-transform ${sortDirection === 'desc' ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {data.map((row, idx) => (
            <tr 
              key={row.id ?? idx} 
              onClick={() => onRowClick?.(row)}
              className={`
                transition-colors duration-150
                ${onRowClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800' : ''}
              `}
            >
              {actions.length > 0 && (
                <td className="px-2 py-2 sticky left-0 bg-white dark:bg-gray-900 z-10">
                  <div className="flex gap-1 flex-wrap justify-center items-center">
                    {actions.map((action, i) => {
                      const isDisabled = action.disabled?.(row) ?? false;
                      const button = action.customButton ? (
                        <div key={i}>{action.customButton}</div>
                      ) : (
                        <button
                          key={i}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isDisabled) action.onClick(row);
                          }}
                          disabled={isDisabled}
                          title={isDisabled ? action.disabledTooltip : undefined}
                          className={`
                            inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md font-medium
                            transition-all duration-200
                            ${action.className || "bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50"}
                            ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                          `}
                        >
                          {action.icon && <span className="w-3 h-3">{action.icon}</span>}
                          {action.label}
                        </button>
                      );
                      return button;
                    })}
                  </div>
                </td>
              )}

              {tableColumns.map((col) => (
                <td
                  key={String(col)}
                  className={`
                    px-3 py-2 text-sm text-gray-700 dark:text-gray-300 
                    break-words whitespace-normal
                    ${columnWidths[col] ?? "max-w-screen"}
                  `}
                >
                  {switchColumns.includes(col) && handleToggle ? (
                    <MySwitch
                      checked={Boolean(getNestedValue(row, col))}
                      onChange={() => handleToggle(row, col)}
                      label={getNestedValue(row, col) ? "On" : "Off"}
                      labelPosition="right"
                    />
                  ) : isDateColumn(String(col)) ? (
                    formatDate(getNestedValue(row, col))
                  ) : isImageColumn(String(col), getNestedValue(row, col)) ? (
                    <img 
                      src={getNestedValue(row, col)} 
                      alt="Preview" 
                      className="w-10 h-10 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
                    />
                  ) : typeof getNestedValue(row, col) === "object" && getNestedValue(row, col) !== null ? (
                    <pre className="text-xs overflow-x-auto max-w-md p-1 bg-gray-50 dark:bg-gray-800 rounded">
                      {JSON.stringify(getNestedValue(row, col), null, 2)}
                    </pre>
                  ) : (
                    <span className="block">
                      {formatValue(getNestedValue(row, col))}
                    </span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Helper function to check if value is an image URL
function isImageColumn(columnName: string, value: any): boolean {
  if (typeof value !== 'string') return false;
  const imageColumns = ['avatar', 'photo', 'image', 'picture', 'thumbnail', 'profile_pic'];
  return imageColumns.some(imgCol => columnName.toLowerCase().includes(imgCol)) && 
         (value.startsWith('http') || value.startsWith('/api/'));
}

// Helper function to check if column is a date column
function isDateColumn(columnName: string): boolean {
  const dateColumns = [
    'start_datetime', 'end_datetime', 'created_at', 'updated_at', 
    'expire_at', 'deleted_at', 'last_login', 'birth_date', 'date'
  ];
  return dateColumns.includes(columnName);
}

// Access nested property by path (e.g., "resource.resource_uid")
function getNestedValue(obj: any, path: string | (string | number)[]) {
  if (!obj) return null;
  const parts = Array.isArray(path) ? path : path.split(".");
  return parts.reduce((acc, part) => (acc ? acc[part] : undefined), obj);
}

// Format value with proper null/undefined handling
function formatValue(value: any): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === 'boolean') return value ? "Yes" : "No";
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'string' && !value.trim()) return "-";
  return String(value);
}

// Convert timestamp to readable date with multiple format support
function formatDate(timestamp?: number | string | null): string {
  if (!timestamp) return "-";
  
  try {
    let date: Date;
    
    // Handle different timestamp formats
    if (typeof timestamp === 'number') {
      // Check if timestamp is in seconds (Unix) or milliseconds
      date = new Date(timestamp < 10000000000 ? timestamp * 1000 : timestamp);
    } else if (typeof timestamp === 'string') {
      // Try to parse as ISO string or timestamp string
      if (!isNaN(Number(timestamp))) {
        const num = Number(timestamp);
        date = new Date(num < 10000000000 ? num * 1000 : num);
      } else {
        date = new Date(timestamp);
      }
    } else {
      return "-";
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) return "-";
    
    // Format based on age of date
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      // Today: show time only
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      // Yesterday
      return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      // Within a week: show day and time
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else if (diffDays < 365) {
      // Within a year: show month and day
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    } else {
      // Older: show full date
      return date.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
    }
  } catch (error) {
    return "-";
  }
}